import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router-dom";
import useAuthentication from "../AuthContext/AuthContext";
import EngagementError from "../ErrorScreens/EngagementError";
import Footer from "../Footer";
import { ScreenLoader, SignInLoader, SignOutLoader } from "../LoadingSpinner";

interface ProtectedRouteElementProps {
  children: JSX.Element;
}

const ProtectedRouteElement = ({ children }: ProtectedRouteElementProps): JSX.Element => {
  const [initialized, setInitialized] = useState(false);
  const { selectedBRF, setSelectedBRF, brfData, selectedEngagement } = useAuthentication();
  const location = useLocation();
  const auth = useAuth();

  const paths = {
    brfselect: "/association/select",
    login: "/authentication/login",
    loginabout: "/authentication/about",
    onboarding: "/onboarding",
    subscription: "/subscription",
    userinfo: "/user/information",
  };

  // This is the primary initialization effect
  useEffect(() => {
    // Only run initialization once when the component mounts or when auth completes loading
    if (initialized || auth.isLoading) {
      return;
    }

    // If not authenticated, mark as initialized and let the normal flow handle it
    if (!auth.isAuthenticated) {
      setInitialized(true);
      return;
    }

    // At this point we are authenticated but not initialized
    const initializeState = async () => {
      try {
        // Check for a saved BRF ID in localStorage
        const savedBrfId = localStorage.getItem("brf");
        
        // If we have BRF data and a saved ID, try to restore it
        if (savedBrfId && brfData?.data?.accounts) {
          const foundBRF = brfData.data.accounts.find(
            account => 
              account.companyRegistrationNumber === savedBrfId || 
              account.id === savedBrfId
          );
          
          if (foundBRF) {
            setSelectedBRF(foundBRF);
          }
          
        } else if (!brfData?.data) {
            return; // Return early without marking initialized if data isn't ready
        }

        // Mark initialization complete
        setInitialized(true);
      } catch (error) {
          console.error("Error during initialization:", error);
        // Mark as initialized even in error case to avoid getting stuck
        setInitialized(true);
      }
    };

    initializeState();
  }, [auth.isAuthenticated, auth.isLoading, brfData?.data, initialized, setSelectedBRF]);

  // IMPORTANT: Add a safety timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!initialized) {
        setInitialized(true);
      }
    }, 10000); // 10 seconds safety timeout
    
    return () => clearTimeout(timeout);
  }, [initialized]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Show loading screen while initializing or auth is loading
  // IMPORTANT: don't show for login page
  if ((!initialized || auth.isLoading) &&  !location.pathname.startsWith("/authentication/login")) {
    return <ScreenLoader />;
  }

  // Handle special auth states
  if (auth.activeNavigator === "signinSilent") 
    return <SignInLoader />;

  if (auth.activeNavigator === "signoutRedirect") 
    return <SignOutLoader />;

  // Handle error states
  if (auth.error) {
    if (auth.error.message === "invalid_grant") {
      return <Navigate to="/authentication/login" replace />;
    }
    return <div>Oops... {auth.error.message}</div>;
  }

  // Only redirect to login if we've fully checked auth state
  if (!auth.isAuthenticated) {
    return <Navigate to="/authentication/login" replace />;
  }

  // Handle BRF data errors
  if (brfData?.isError || (brfData?.data?.accounts?.length === 0 &&  brfData?.data?.engagements?.length === 0)) {
    return (
      <>
        <EngagementError />
        <Footer />
      </>
    );
  }

  // Handle BRF selection states
  // This now only runs after we've initialized properly
  if (!selectedBRF && !selectedEngagement) {
    const isAllowedPath = [
      paths.subscription,
      paths.login,
      paths.loginabout,
      paths.brfselect
    ].includes(location.pathname);

    if (!isAllowedPath) {
      return <Navigate to={paths.brfselect} replace />;
    }
  }

  // Handle subscription states
  if (!selectedBRF && selectedEngagement) {
    const isAllowedPath = [
      paths.subscription,
      paths.login,
      paths.loginabout
    ].includes(location.pathname);

    if (!isAllowedPath) {
      return <Navigate to={paths.subscription} replace />;
    }
  }

  // Handle onboarding states - check if selected BRF needs onboarding
  if (selectedBRF && (
    selectedBRF.accountStatus?.value === "Onboarding" ||
    selectedBRF.accountStatus?.value === "New" ||
    selectedBRF.accountStatus?.id === 0 ||
    selectedBRF.accountStatus?.id === 1
  )) {
    const isAllowedPath = [
      paths.onboarding,
      paths.login,
      paths.loginabout,
      paths.brfselect,
      paths.userinfo
    ].includes(location.pathname);

    if (!isAllowedPath) {
      return <Navigate to={paths.onboarding} replace />;
    }
  }

  // Handle inactive states - check if selected BRF is inactive
  if (selectedBRF && selectedBRF.accountStatus?.value === "Inactive") {
    const isAllowedPath = [
      paths.subscription,
      paths.login,
      paths.loginabout,
      paths.brfselect
    ].includes(location.pathname);

    if (!isAllowedPath) {
      return <Navigate to={paths.subscription} replace />;
    }
  }
  return (
    <>
      {children}
      <Footer />
    </>
  );
};

export default ProtectedRouteElement;